/**
 * SincronizacaoService
 *
 * Responsável por:
 * 1. Sincronização inicial: baixar produtos e clientes da API e persistir no SQLite.
 * 2. Sincronização de vendas pendentes: enviar vendas criadas offline para a API.
 *
 * Só é executado no modo desktop (Electron). No modo web é no-op.
 */

import { isDesktopMode, getElectronAPI } from "@/plugins/desktop";

/**
 * Monta o payload exigido pelo endpoint POST /api/offline/vendas/sincronizar.
 *
 * Colunas locais (vendas_local)  →  campos do backend (nfe)
 *   numero                       →  numero_venda
 *   payload_json                 →  dados  (documento NFCe completo)
 *   valorTotal                   →  valorTotal
 *   numeroNota                   →  numeroNota
 *   identificacaoCliente         →  identificacaoCliente
 *   nomeCliente                  →  nomeCliente
 *   dtVenda                      →  dtVenda
 */
function montarPayloadVenda(venda) {
  let dadosExtras = {};
  try { dadosExtras = venda.payload_json ? JSON.parse(venda.payload_json) : {}; } catch { /**/ }

  return {
    uuid_venda_terminal:  venda.uuid_venda_terminal,
    numero_venda:         venda.numero               ?? null,
    dados:                dadosExtras.dados          ?? venda.payload_json ?? null,
    valorTotal:           venda.valorTotal,
    status:               venda.status               ?? null,
    modelo:               venda.modelo               ?? 65,
    serie:                venda.serie                ?? null,
    chave:                dadosExtras.chave          ?? null,
    numeroNota:           venda.numeroNota           ?? null,
    identificacaoCliente: venda.identificacaoCliente ?? null,
    nomeCliente:          venda.nomeCliente          ?? null,
    dtVenda:              venda.dtVenda              ?? null,
    emissao:              venda.emissao              ?? null,
    id_terminal_venda:    venda.id_terminal_venda    ?? null,
    id_vendedor:          venda.id_vendedor          ?? dadosExtras.dados?.id_vendedor ?? null,
    observation:          dadosExtras.dados?.observation ?? dadosExtras.dados?.observacao
      ?? dadosExtras.observation ?? dadosExtras.observacao ?? null,
    origem_venda:         venda.origem_venda         ?? "pdv_offline",
  };
}

const SincronizacaoService = {
  /**
   * Sincronização inicial ao configurar o terminal.
   * Baixa produtos e clientes da API e salva localmente.
   *
   * @param {object} options
   * @param {string} options.apiUrl - URL base da API
   * @param {string} options.token  - Bearer token
   * @param {function} options.onProgress - callback(percent, statusText)
   */
  async sincronizarInicial({ apiUrl, token, onProgress }) {
    if (!isDesktopMode()) return;

    const api = getElectronAPI();
    const baseUrl = apiUrl.replace(/\/$/, "");
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    };

    const progress = (pct, msg) => {
      if (typeof onProgress === "function") onProgress(pct, msg);
    };

    const idEmpresaRaw = await api.config.get("id_empresa");
    const id_empresa   = idEmpresaRaw ? parseInt(idEmpresaRaw, 10) : null;

    // ── Produtos ──────────────────────────────────────────────────────────
    progress(5, "Baixando produtos...");
    try {
      let pagina = 1;
      let totalPaginas = 1;
      let totalBaixados = 0;

      do {
        const resp = await fetch(`${baseUrl}/api/offline/produtos?page=${pagina}&per_page=200`, {
          headers,
        });

        if (!resp.ok) {
          throw new Error(`Erro ao buscar produtos (HTTP ${resp.status})`);
        }

        const json = await resp.json();
        const items = json.data ?? json ?? [];
        totalPaginas = json.last_page ?? json.meta?.last_page ?? 1;

        if (items.length > 0) {
          // Normaliza id_remoto para cada produto
          const normalized = items.map((p) => ({
            ...p,
            id_empresa: p.id_empresa ?? id_empresa,
            id_remoto: String(p.id ?? p.id_remoto ?? ""),
          }));
          await api.produtos.upsertMany(normalized);

          // Salva lotes dos produtos que possuem a propriedade lotes
          const todosLotes = normalized.flatMap((p) =>
            Array.isArray(p.lotes) ? p.lotes : []
          );
          if (todosLotes.length > 0) {
            await api.produtoLotes.salvarLotes(todosLotes);
          }

          totalBaixados += items.length;
        }

        const pct = 5 + Math.round((pagina / totalPaginas) * 40);
        progress(pct, `Produtos: ${totalBaixados} registros baixados...`);
        pagina++;
      } while (pagina <= totalPaginas);

      await api.sync.atualizarControle("produtos", new Date().toISOString());
      progress(45, `Produtos sincronizados: ${totalBaixados}`);
    } catch (e) {
      console.error("[Sync] Erro ao sincronizar produtos:", e);
      progress(45, `Atenção: erro ao baixar produtos (${e.message})`);
    }

    // ── Clientes ──────────────────────────────────────────────────────────
    progress(50, "Baixando clientes...");
    try {
      let pagina = 1;
      let totalPaginas = 1;
      let totalBaixados = 0;

      do {
        const resp = await fetch(`${baseUrl}/api/offline/clientes?page=${pagina}&per_page=200`, {
          headers,
        });

        if (!resp.ok) {
          throw new Error(`Erro ao buscar clientes (HTTP ${resp.status})`);
        }

        const json = await resp.json();
        const items = json.data ?? json ?? [];
        totalPaginas = json.last_page ?? json.meta?.last_page ?? 1;

        if (items.length > 0) {
          const normalized = items.map((c) => ({
            ...c,
            id_empresa: c.id_empresa ?? id_empresa,
            id_remoto: String(c.id ?? c.id_remoto ?? ""),
          }));
          await api.clientes.upsertMany(normalized);
          totalBaixados += items.length;
        }

        const pct = 50 + Math.round((pagina / totalPaginas) * 40);
        progress(pct, `Clientes: ${totalBaixados} registros baixados...`);
        pagina++;
      } while (pagina <= totalPaginas);

      await api.sync.atualizarControle("clientes", new Date().toISOString());
      progress(90, `Clientes sincronizados: ${totalBaixados}`);
    } catch (e) {
      console.error("[Sync] Erro ao sincronizar clientes:", e);
      progress(90, `Atenção: erro ao baixar clientes (${e.message})`);
    }

    // ── Formas de Pagamento ───────────────────────────────────────────────
    progress(92, "Baixando formas de pagamento...");
    try {
      const resp = await fetch(`${baseUrl}/api/offline/formas-pagamento`, { headers });

      if (!resp.ok) {
        throw new Error(`Erro ao buscar formas de pagamento (HTTP ${resp.status})`);
      }

      const json = await resp.json();
      const items = json.data ?? json ?? [];

      if (items.length > 0) {
        await api.formasPagamento.salvar(items);
      }

      await api.sync.atualizarControle("formas_pagamento", new Date().toISOString());
      progress(97, `Formas de pagamento sincronizadas: ${items.length}`);
    } catch (e) {
      console.error("[Sync] Erro ao sincronizar formas de pagamento:", e);
      progress(97, `Atenção: erro ao baixar formas de pagamento (${e.message})`);
    }

    // ── Listas de Preço ────────────────────────────────────────────────────
    progress(98, "Baixando listas de preço...");
    try {
      let pagina = 1;
      let totalPaginas = 1;
      let totalBaixados = 0;

      do {
        const resp = await fetch(
          `${baseUrl}/api/offline/lista-precos?page=${pagina}&per_page=50`,
          { headers }
        );

        if (!resp.ok) {
          throw new Error(`Erro ao buscar listas de preço (HTTP ${resp.status})`);
        }

        const json = await resp.json();
        const listas = json.data ?? json ?? [];
        totalPaginas = json.last_page ?? json.meta?.last_page ?? 1;

        if (listas.length > 0) {
          await api.listaPreco.salvar(listas);
          totalBaixados += listas.length;
        }

        pagina++;
      } while (pagina <= totalPaginas);

      await api.sync.atualizarControle("lista_preco", new Date().toISOString());
      progress(99, `Listas de preço sincronizadas: ${totalBaixados}`);
    } catch (e) {
      console.error("[Sync] Erro ao sincronizar listas de preço:", e);
      progress(99, `Atenção: erro ao baixar listas de preço (${e.message})`);
    }

    // ── Combinações de Produto ────────────────────────────────────────────
    progress(99, "Baixando combinações de produto...");
    try {
      const controles      = await api.sync.getControle();
      const controleComb   = (controles || []).find((c) => c.entidade === "produtos_combinacoes");
      const updatedAfterQ  = controleComb && controleComb.ultimo_sync
        ? `&updated_after=${encodeURIComponent(controleComb.ultimo_sync)}`
        : "";

      let pagina = 1, totalPaginas = 1, totalBaixados = 0;

      do {
        const url  = `${baseUrl}/api/offline/produtos-combinacoes?page=${pagina}&per_page=200${updatedAfterQ}`;
        const resp = await fetch(url, { headers });

        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

        const json  = await resp.json();
        const items = json.data ?? json ?? [];
        totalPaginas = json.last_page ?? json.meta?.last_page ?? 1;

        if (items.length > 0) {
          await api.produtoCombinacoes.salvar(items);
          totalBaixados += items.length;
        }

        pagina++;
      } while (pagina <= totalPaginas);

      await api.sync.atualizarControle("produtos_combinacoes", new Date().toISOString());
    } catch (e) {
      console.error("[Sync] Erro ao sincronizar combinações:", e);
    }

    progress(100, "Sincronização concluída!");
  },

  /**
   * Tenta enviar as vendas pendentes (modo offline) para a API em lote.
   * Endpoint: POST /api/offline/vendas/sincronizar
   *
   * @param {string} apiUrl
   * @param {string} token
   * @returns {Promise<{ enviadas: number, erros: number }>}
   */
  async sincronizarVendasPendentes(apiUrl, token) {
    if (!isDesktopMode()) return { enviadas: 0, erros: 0 };

    const api = getElectronAPI();
    const baseUrl = apiUrl.replace(/\/$/, "");
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    };

    const pendentes = await api.vendas.getPendentesSync();
    if (!pendentes || pendentes.length === 0) return { enviadas: 0, erros: 0 };

    const uuid_terminal = await api.config.get("uuid_terminal");

    // Monta lote
    const vendas = pendentes.map((venda) => {
      let dadosExtras = {};
      try { dadosExtras = venda.payload_json ? JSON.parse(venda.payload_json) : {}; } catch { /**/ }

      return {
        uuid_venda_terminal:  venda.uuid_venda_terminal,
        numero:               venda.numero               ?? null,
        numeroNota:           venda.numeroNota           ?? null,
        serie:                venda.serie                ?? null,
        modelo:               venda.modelo               ?? 65,
        finalidade:           venda.finalidade           ?? 1,
        tipoNF:               venda.tipoNF               ?? 1,
        valorTotal:           venda.valorTotal,
        dtVenda:              venda.dtVenda              ?? null,
        emissao:              venda.emissao              ?? null,
        identificacaoCliente: venda.identificacaoCliente ?? null,
        nomeCliente:          venda.nomeCliente          ?? null,
        origem_venda:         venda.origem_venda         ?? "pdv_offline",
        isPDVSync:            true,
        sync_app:             true,
        id_terminal_venda:    venda.id_terminal_venda    ?? null,
        id_vendedor:          venda.id_vendedor          ?? dadosExtras.dados?.id_vendedor
          ?? dadosExtras.id_vendedor ?? null,
        observation:          dadosExtras.dados?.observation ?? dadosExtras.dados?.observacao
          ?? dadosExtras.observation ?? dadosExtras.observacao ?? null,
        observacao:           dadosExtras.dados?.observation ?? dadosExtras.dados?.observacao
          ?? dadosExtras.observation ?? dadosExtras.observacao ?? null,
        dados:                dadosExtras.dados          ?? dadosExtras ?? null,
      };
    });

    let enviadas = 0;
    let erros = 0;

    try {
      const resp = await fetch(`${baseUrl}/api/offline/vendas/sincronizar`, {
        method: "POST",
        headers,
        body: JSON.stringify({ uuid_terminal, vendas }),
      });

      if (!resp.ok) {
        const errBody = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${errBody}`);
      }

      const json = await resp.json();
      const resultados = json.resultados ?? json.data ?? [];

      for (const resultado of resultados) {
        const uuid = resultado.uuid_venda_terminal;
        const status = resultado.status ?? "";

        if (status === "sincronizada" || status === "ja_sincronizada") {
          await api.vendas.marcarSincronizada(uuid, resultado);
          enviadas++;
        } else {
          const erro = resultado.erro ?? resultado.message ?? "Erro desconhecido";
          await api.vendas.marcarErro(uuid, erro);
          erros++;
        }
      }

      // Vendas que não vieram no retorno → marca como erro
      const uuidsRetorno = resultados.map((r) => r.uuid_venda_terminal);
      for (const venda of pendentes) {
        if (!uuidsRetorno.includes(venda.uuid_venda_terminal)) {
          await api.vendas.marcarErro(venda.uuid_venda_terminal, "Sem retorno do servidor");
          erros++;
        }
      }
    } catch (e) {
      console.error("[Sync] Erro ao enviar lote de vendas:", e);
      for (const venda of pendentes) {
        await api.vendas.marcarErro(venda.uuid_venda_terminal, e.message);
      }
      erros = pendentes.length;
    }

    return { enviadas, erros };
  },

  /**
   * Atualiza produtos e clientes do servidor (sync incremental).
   * Usa o campo `updated_at` da última sync como filtro.
   *
   * @param {string} apiUrl
   * @param {string} token
   */
  async sincronizarIncremental(apiUrl, token) {
    if (!isDesktopMode()) return;

    const api = getElectronAPI();
    const baseUrl = apiUrl.replace(/\/$/, "");
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    };

    const controles = await api.sync.getControle();
    const getUltimoSync = (entidade) => {
      const item = controles.find((c) => c.entidade === entidade);
      return item?.ultimo_sync ?? null;
    };

    // Produtos incrementais
    const ultimoProdutos = getUltimoSync("produtos");
    if (ultimoProdutos) {
      try {
        const resp = await fetch(
          `${baseUrl}/api/offline/produtos?updated_after=${encodeURIComponent(ultimoProdutos)}&per_page=500`,
          { headers }
        );
        if (resp.ok) {
          const json = await resp.json();
          const items = json.data ?? json ?? [];
          if (items.length > 0) {
            const normalized = items.map((p) => ({
              ...p,
              id_remoto: String(p.id ?? p.id_remoto ?? ""),
            }));
            await api.produtos.upsertMany(normalized);
            await api.sync.atualizarControle("produtos", new Date().toISOString());
          }
        }
      } catch (e) {
        console.error("[Sync] Erro no sync incremental de produtos:", e);
      }
    }

    // Clientes incrementais
    const ultimoClientes = getUltimoSync("clientes");
    if (ultimoClientes) {
      try {
        const resp = await fetch(
          `${baseUrl}/api/offline/clientes?updated_after=${encodeURIComponent(ultimoClientes)}&per_page=500`,
          { headers }
        );
        if (resp.ok) {
          const json = await resp.json();
          const items = json.data ?? json ?? [];
          if (items.length > 0) {
            const normalized = items.map((c) => ({
              ...c,
              id_remoto: String(c.id ?? c.id_remoto ?? ""),
            }));
            await api.clientes.upsertMany(normalized);
            await api.sync.atualizarControle("clientes", new Date().toISOString());
          }
        }
      } catch (e) {
        console.error("[Sync] Erro no sync incremental de clientes:", e);
      }
    }

    // Formas de pagamento incrementais
    const ultimoFormas = getUltimoSync("formas_pagamento");
    if (ultimoFormas) {
      try {
        const resp = await fetch(
          `${baseUrl}/api/offline/formas-pagamento?updated_after=${encodeURIComponent(ultimoFormas)}`,
          { headers }
        );
        if (resp.ok) {
          const json = await resp.json();
          const items = json.data ?? json ?? [];
          if (items.length > 0) {
            await api.formasPagamento.salvar(items);
            await api.sync.atualizarControle("formas_pagamento", new Date().toISOString());
          }
        }
      } catch (e) {
        console.error("[Sync] Erro no sync incremental de formas de pagamento:", e);
      }
    }

    // Listas de preço incrementais
    const ultimoListaPreco = getUltimoSync("lista_preco");
    if (ultimoListaPreco) {
      try {
        const resp = await fetch(
          `${baseUrl}/api/offline/lista-precos?updated_after=${encodeURIComponent(ultimoListaPreco)}&per_page=100`,
          { headers }
        );
        if (resp.ok) {
          const json = await resp.json();
          const listas = json.data ?? json ?? [];
          if (listas.length > 0) {
            await api.listaPreco.salvar(listas);
            await api.sync.atualizarControle("lista_preco", new Date().toISOString());
          }
        }
      } catch (e) {
        console.error("[Sync] Erro no sync incremental de listas de preço:", e);
      }
    }
  },
};

export default SincronizacaoService;
